import type { PaymentResponse } from '@monorepo/shared/schemas/payments'
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  DollarSign,
  User,
  XCircle,
} from 'lucide-react'
import { useState } from 'react'
import {
  SubtleTabs,
  SubtleTabsContent,
  SubtleTabsList,
  SubtleTabsTrigger,
} from '@/shared/components/common/tabs/SubtleTabs'
import { Badge } from '@/shared/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'

interface PaymentDetailsDialogProps {
  payment: PaymentResponse | null
  onClose: () => void
}

function getStatusVariant(
  status: PaymentResponse['status']
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'succeeded':
      return 'default'
    case 'pending':
      return 'secondary'
    case 'failed':
      return 'destructive'
    case 'refunded':
    case 'canceled':
      return 'outline'
    default:
      return 'outline'
  }
}

function getStatusLabel(status: PaymentResponse['status']): string {
  if (status === 'succeeded') return 'Completed'
  if (status === 'canceled') return 'Canceled'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

function getStatusIcon(status: PaymentResponse['status']) {
  switch (status) {
    case 'succeeded':
      return <CheckCircle className='w-4 h-4' />
    case 'pending':
      return <Clock className='w-4 h-4' />
    case 'failed':
      return <XCircle className='w-4 h-4' />
    case 'refunded':
    case 'canceled':
      return <AlertCircle className='w-4 h-4' />
    default:
      return <AlertCircle className='w-4 h-4' />
  }
}

function formatCurrency(amount: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount)
}

function formatDate(dateString: string | null) {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatMethodLabel(method: string | null) {
  if (!method) return '—'
  return method
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function DetailRow({
  label,
  value,
  className,
}: {
  label: string
  value: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={`flex justify-between items-start gap-4 py-2 ${className ?? ''}`}
    >
      <span className='text-sm text-muted-foreground shrink-0'>{label}</span>
      <span className='text-sm font-medium text-right'>{value}</span>
    </div>
  )
}

export function PaymentDetailsDialog({
  payment,
  onClose,
}: PaymentDetailsDialogProps) {
  const [activeTab, setActiveTab] = useState('overview')

  if (!payment) return null

  const hasMetadata =
    payment.metadata && Object.keys(payment.metadata).length > 0

  return (
    <Dialog open={!!payment} onOpenChange={open => !open && onClose()}>
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <DollarSign className='w-5 h-5' />
            Payment Details
          </DialogTitle>
        </DialogHeader>

        <SubtleTabs value={activeTab} onValueChange={setActiveTab}>
          <SubtleTabsList>
            <SubtleTabsTrigger value='overview'>Overview</SubtleTabsTrigger>
            <SubtleTabsTrigger value='details'>Details</SubtleTabsTrigger>
          </SubtleTabsList>

          {/* Overview Tab */}
          <SubtleTabsContent value='overview'>
            <div className='space-y-1'>
              {/* Amount — prominent */}
              <div className='text-center pb-4 mb-2 border-b'>
                <div className='text-3xl font-bold'>
                  {formatCurrency(payment.amount, payment.currency)}
                </div>
                <div className='text-sm text-muted-foreground mt-1'>
                  {payment.currency.toUpperCase()}
                </div>
                {payment.refunded_amount > 0 && (
                  <div className='text-sm text-red-500 mt-1'>
                    {formatCurrency(payment.refunded_amount)} refunded
                  </div>
                )}
              </div>

              {/* Payment ID + Status */}
              <DetailRow
                label='Payment Number'
                value={
                  <span className='font-mono text-xs'>
                    {payment.payment_id}
                  </span>
                }
              />
              <DetailRow
                label='Status'
                value={
                  <Badge
                    variant={getStatusVariant(payment.status)}
                    className='gap-1'
                  >
                    {getStatusIcon(payment.status)}
                    {getStatusLabel(payment.status)}
                  </Badge>
                }
              />

              {/* Customer */}
              <div className='border-t pt-2 mt-2'>
                <div className='flex items-center gap-1 text-xs text-muted-foreground font-medium mb-1 uppercase tracking-wide'>
                  <User className='w-3 h-3' />
                  Customer
                </div>
                <DetailRow label='Name' value={payment.customer?.name ?? '—'} />
                <DetailRow
                  label='Email'
                  value={payment.customer?.email ?? '—'}
                />
              </div>

              {/* Payment Method + Date */}
              <div className='border-t pt-2 mt-2'>
                <div className='flex items-center gap-1 text-xs text-muted-foreground font-medium mb-1 uppercase tracking-wide'>
                  <CreditCard className='w-3 h-3' />
                  Payment Info
                </div>
                <DetailRow
                  label='Method'
                  value={formatMethodLabel(payment.payment_method)}
                />
                <DetailRow
                  label='Processed'
                  value={
                    <div className='flex items-center gap-1'>
                      <Calendar className='w-3 h-3 text-muted-foreground' />
                      {formatDate(payment.processed_at)}
                    </div>
                  }
                />
              </div>

              {/* Invoice + Notes */}
              {payment.invoice_id && (
                <DetailRow label='Invoice ID' value={payment.invoice_id} />
              )}
              {payment.notes && (
                <DetailRow
                  label='Notes'
                  value={
                    <span className='text-xs whitespace-pre-wrap'>
                      {payment.notes}
                    </span>
                  }
                />
              )}
            </div>
          </SubtleTabsContent>

          {/* Details Tab */}
          <SubtleTabsContent value='details'>
            <div className='space-y-1'>
              <DetailRow
                label='Organization ID'
                value={
                  <span className='font-mono text-xs'>
                    {payment.organization_id ?? '—'}
                  </span>
                }
              />
              <DetailRow
                label='Fees'
                value={formatCurrency(payment.fees, payment.currency)}
              />
              <DetailRow
                label='Net Amount'
                value={
                  payment.net_amount != null
                    ? formatCurrency(payment.net_amount, payment.currency)
                    : '—'
                }
              />
              {payment.external_payment_id && (
                <DetailRow
                  label='External Payment ID'
                  value={
                    <span className='font-mono text-xs'>
                      {payment.external_payment_id}
                    </span>
                  }
                />
              )}
              {payment.failure_reason && (
                <DetailRow
                  label='Failure Reason'
                  value={
                    <span className='text-red-500 text-xs'>
                      {payment.failure_reason}
                    </span>
                  }
                />
              )}
              {hasMetadata && (
                <div className='pt-2'>
                  <div className='text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1'>
                    Metadata
                  </div>
                  <pre className='bg-muted rounded p-2 text-xs overflow-auto max-h-32'>
                    {JSON.stringify(payment.metadata, null, 2)}
                  </pre>
                </div>
              )}
              <div className='border-t pt-2 mt-2'>
                <DetailRow
                  label='Created At'
                  value={formatDate(payment.created_at)}
                />
                <DetailRow
                  label='Updated At'
                  value={formatDate(payment.updated_at)}
                />
              </div>
            </div>
          </SubtleTabsContent>
        </SubtleTabs>
      </DialogContent>
    </Dialog>
  )
}
